import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Attendance from './pages/Attendance';
import Leaves from './pages/Leaves';
import Payroll from './pages/Payroll';
import Performance from './pages/Performance';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import ApprovalHierarchy from './pages/ApprovalHierarchy';
import Institutes from './pages/Institutes';
import Documents from './pages/Documents';
import Notices from './pages/Notices';
import LeavePolicy from './pages/LeavePolicy';
import DepartmentMapping from './pages/DepartmentMapping';
import Recruitment from './pages/Recruitment';
import Onboarding from './pages/Onboarding';
import PublicApplication from './pages/PublicApplication';
import DataIntegrity from './pages/DataIntegrity';
import AIReport from './pages/AIReport';
import LearningDevelopment from './pages/LearningDevelopment';
import AuditTrail from './pages/AuditTrail';
import SalaryRevision from './pages/SalaryRevision';
import SalaryTemplates from './pages/SalaryTemplates';
import ExcelSalaryTemplates from './pages/ExcelSalaryTemplates';
import AdminPanel from './pages/AdminPanel';
import Branches from './pages/Branches';
import UserManual from './pages/UserManual';
import MyPayrollReport from './pages/MyPayrollReport';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { InstallAppPrompt } from './components/InstallAppPrompt';

function RoleGuard({ roles, children }) {
  const { user, token } = useAuth();
  // If token exists but user not yet loaded (page refresh), wait
  if (token && !user) return null;
  if (!user || !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Public: Onboarding form (no auth) */}
      <Route path="/onboarding/:token" element={<Onboarding />} />
      {/* Public: Recruitment Application form (no auth, accessible via QR/link) */}
      <Route path="/apply/:jobCode" element={<PublicApplication />} />
      
      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/employees" element={<Employees />} />
          <Route path="/employees/:employeeId" element={<EmployeeDetail />} />
          <Route path="/attendance" element={<Attendance />} />
          <Route path="/leaves" element={<Leaves />} />
          <Route path="/payroll" element={<Payroll />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/notices" element={<Notices />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/roles" element={<RoleManagement />} />
          <Route path="/institutes" element={<Institutes />} />
          <Route path="/approval-hierarchy" element={<ApprovalHierarchy />} />
          <Route path="/leave-policy" element={<LeavePolicy />} />
          <Route path="/department-mapping" element={<DepartmentMapping />} />
          <Route path="/recruitment" element={<Recruitment />} />
          <Route path="/data-integrity" element={<RoleGuard roles={['super_admin','admin','hr']}><DataIntegrity /></RoleGuard>} />
          <Route path="/learning-development" element={<LearningDevelopment />} />
          <Route path="/audit-trail" element={<RoleGuard roles={['super_admin','admin','hr']}><AuditTrail /></RoleGuard>} />
          <Route path="/salary-revisions" element={<RoleGuard roles={['super_admin','admin','hr','finance','principal']}><SalaryRevision /></RoleGuard>} />
          <Route path="/excel-salary-templates" element={<RoleGuard roles={['super_admin','admin','hr','finance','finance_head','branch_admin','institute_admin']}><ExcelSalaryTemplates /></RoleGuard>} />
          <Route path="/admin-panel" element={<RoleGuard roles={['super_admin']}><AdminPanel /></RoleGuard>} />
          <Route path="/branches" element={<RoleGuard roles={['super_admin','admin','hr','branch_admin']}><Branches /></RoleGuard>} />
          <Route path="/user-manual" element={<UserManual />} />
          <Route path="/my-report" element={<MyPayrollReport />} />
          <Route path="/ai-report" element={<AIReport />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <InstallAppPrompt />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
